import { useCallback } from 'react';
import { AFFILIATE_CLICK_URL } from '@/utils/constants';
import {
  buildClickPayload,
  type ClickParams,
  type AffiliateClickPayload,
} from '@/utils/revenueAttribution';

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    gtag_report_conversion?: (url?: string) => void;
  }
}

/**
 * Centralized affiliate click tracking hook.
 *
 * Replaces duplicated click handlers across FirmTableRow, FeaturedFirms,
 * BestDealBanner, FirmDetail, and StickyAffiliateCTA.
 *
 * Handles:
 * 1. Supabase edge function POST (with click_id for revenue attribution)
 * 2. GA4 affiliate_click event
 * 3. Google Ads conversion tracking
 * 4. Opening the affiliate URL in a new tab
 *
 * Usage:
 * ```tsx
 * const { trackClick } = useAffiliateClick();
 *
 * const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
 *   e.preventDefault();
 *   trackClick({
 *     firmName: firm.name,
 *     accountSize: '50k',
 *     discountCode: firm.coupon_code,
 *     affiliateUrl: targetUrl,
 *     source: 'comparison_table',
 *   });
 * };
 * ```
 */
export function useAffiliateClick() {
  const trackClick = useCallback((params: ClickParams & { skipNavigation?: boolean }) => {
    const payload = buildClickPayload(params);

    // 1. POST to Supabase edge function (fire-and-forget)
    sendToEdgeFunction(payload);

    // 2. GA4 event
    fireGA4Event(payload);

    // 3. Google Ads conversion
    if (window.gtag_report_conversion) {
      window.gtag_report_conversion();
    }

    // 4. Open affiliate URL (skip if the <a> tag handles navigation natively)
    if (!params.skipNavigation) {
      window.open(params.affiliateUrl, '_blank', 'noopener,noreferrer');
    }
  }, []);

  return { trackClick };
}

// ─── Internal helpers ────────────────────────────────────────

function sendToEdgeFunction(payload: AffiliateClickPayload): void {
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  if (!anonKey) return;

  const body = JSON.stringify(payload);

  // Use sendBeacon for reliable delivery during navigation/unload.
  // sendBeacon can't set custom headers, so fall back to fetch if needed.
  // For Supabase edge functions, we need the Authorization header —
  // so we use fetch with keepalive which survives page transitions.
  try {
    fetch(AFFILIATE_CLICK_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${anonKey}`,
        'apikey': anonKey,
      },
      body,
      keepalive: true, // Survives page unload — same guarantee as sendBeacon
    }).catch(() => {});
  } catch {
    // Final fallback: sendBeacon (no custom headers, edge function must handle)
    try {
      navigator.sendBeacon(AFFILIATE_CLICK_URL, new Blob([body], { type: 'application/json' }));
    } catch {
      // Give up silently
    }
  }
}

function fireGA4Event(payload: AffiliateClickPayload): void {
  if (typeof window.gtag !== 'function') return;

  window.gtag('event', 'affiliate_click', {
    click_id: payload.click_id,
    session_id: payload.session_id,
    firm_name: payload.firm_name,
    account_size: payload.account_size,
    coupon_code: payload.discount_code,
    source: payload.source,
  });
}
